import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Entity {
  id: string
  name: string
  golden_records: number
  source_records: number
  match_rate: number
  duplicates: number
  last_sync: string
  status: string
}

interface MatchRule {
  id: string
  entity: string
  name: string
  type: string
  weight: number
  matches_mtd: number
  accuracy: number
}

interface Statistics {
  master_entities: number
  golden_records: number
  match_rate: number
  merge_rate: number
  duplicates_resolved: number
  data_stewards: number
}

export default function MasterDataPage() {
  const [entities, setEntities] = useState<Entity[]>([])
  const [rules, setRules] = useState<MatchRule[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [entitiesRes, rulesRes, statsRes] = await Promise.all([
        fetch('/api/masterdata/entities'),
        fetch('/api/masterdata/rules'),
        fetch('/api/masterdata/statistics')
      ])
      if (entitiesRes.ok) setEntities(await entitiesRes.json())
      if (rulesRes.ok) setRules(await rulesRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching master data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Master Data Management</h1>
          <p className="mt-2 text-gray-600">Phase 264 - Golden records and MDM</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Master Entities</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.master_entities}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Golden Records</p>
              <p className="text-2xl font-bold text-green-600">{formatNumber(statistics.golden_records)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Match Rate</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.match_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Merge Rate</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.merge_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Duplicates Resolved</p>
              <p className="text-2xl font-bold text-orange-600">{formatNumber(statistics.duplicates_resolved)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Data Stewards</p>
              <p className="text-2xl font-bold text-green-600">{statistics.data_stewards}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Master Data Entities</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Golden Records</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source Records</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Match Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duplicates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Sync</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {entities.map((entity) => (
                  <tr key={entity.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{entity.name}</td>
                    <td className="px-6 py-4 text-green-600 font-bold">{formatNumber(entity.golden_records)}</td>
                    <td className="px-6 py-4 text-blue-600">{formatNumber(entity.source_records)}</td>
                    <td className="px-6 py-4 text-green-600">{entity.match_rate}%</td>
                    <td className="px-6 py-4 text-orange-600">{formatNumber(entity.duplicates)}</td>
                    <td className="px-6 py-4 text-gray-500">{entity.last_sync}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Matching Rules</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rule</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Weight</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matches MTD</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accuracy</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{rule.name}</td>
                    <td className="px-6 py-4 text-gray-500">{rule.entity}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${rule.type === 'Deterministic' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{rule.type}</span>
                    </td>
                    <td className="px-6 py-4 text-purple-600">{rule.weight}</td>
                    <td className="px-6 py-4 text-blue-600">{formatNumber(rule.matches_mtd)}</td>
                    <td className="px-6 py-4 text-green-600 font-bold">{rule.accuracy}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
